import React, { useMemo, useRef, useState } from "react"

import { FeatureFlag } from "@common-shared/config/featureFlags.config"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"

import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element"
import { SpinnerElement } from "@front-shared/elements/spinner.element"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook"

import { GENERAL_CONFIG } from "../../config/general.config"
import { ROUTES_CONFIG } from "../../config/routes.config"
import { RuntimeHelper } from "../../helpers/runtime.helper"
import { useHasPermissions } from "../../hooks/hasPermissions.hook"
import { useIsFeatureFlagEnabled } from "../../hooks/isFeatureFlagEnabled.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import type { IReviewNewRulesLocationState } from "../../types/networkStatus.types"
import { ReviewNewRulesComponent } from "./reviewNewRules/reviewNewRules.component"
import { RulesListComponent } from "./rulesList/rulesList.component"
import { RulesListHeaderComponent } from "./rulesList/rulesListHeader.component"

export const NetworkStatusRulesComponent: React.FunctionComponent = () => {
    useScrollFix()

    useHasPermissions(
        OneVerifyPermission.manageNetworkStatusRules,
        undefined,
        true,
    )

    useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_NETWORK_STATUS_RULES,
        undefined,
        ROUTES_CONFIG.profileUrl,
    )

    const scrollableTableRef = useRef<HTMLDivElement>(null)

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const runtimeState = useAppSelector(runtimeSelector)

    const [reviewState, setReviewState] =
        useState<IReviewNewRulesLocationState | null>(null)

    const [selectedRulesVersion, setSelectedRulesVersion] = useState<
        number | null
    >(null)

    const isListLoading = useMemo<boolean>(() => {
        return (
            RuntimeHelper.isNetworkStatusRulesListLoading() ||
            RuntimeHelper.isNetworkStatusGetVersionsLoading()
        )
    }, [runtimeState.isLoading])

    return (
        <div data-produktly="network-status-page" className="pb-32 relative">
            <PageHelmetElement
                title={
                    reviewState ? "Review New Rules" : "Network Status Rules"
                }
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            {reviewState ? (
                <ReviewNewRulesComponent
                    state={reviewState}
                    onClose={() => {
                        setReviewState(null)
                    }}
                    onReuploadDone={data => {
                        setReviewState(data)
                    }}
                    onAddValidRulesSuccess={() => {
                        setReviewState(null)
                    }}
                />
            ) : selectedPracticeRole ? (
                <>
                    <RulesListHeaderComponent
                        className="mb-[24px]"
                        scrollableTableRef={scrollableTableRef}
                        onValidationDone={data => {
                            setReviewState(data)
                        }}
                        selectedRulesVersion={selectedRulesVersion}
                        onRulesVersionSelected={setSelectedRulesVersion}
                    />

                    <RulesListComponent
                        scrollableTableRef={scrollableTableRef}
                        selectedRulesVersion={selectedRulesVersion}
                        isListLoading={isListLoading}
                    />
                </>
            ) : (
                <SpinnerElement containerClassName="mt-[32px] text-center" />
            )}
        </div>
    )
}
